import React, { useEffect } from 'react'
import { Form, Input, Row, Col, message } from 'antd'
import { useTranslation } from 'react-i18next'
import { Modal } from '~/Components/Modal'
import { OrganizationsService } from '~/Services/OrganizationService'
import { useAppSelector } from '~/Store/hooks'
import { UserSelectors } from '~/Store/User'

interface AddCompanyModalProps {
  visible: boolean
  onClose: () => void
  onSuccess?: () => void
}

export const AddCompanyModal: React.FC<AddCompanyModalProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [loading, setLoading] = React.useState(false)
  const currentUser = useAppSelector(UserSelectors.getCurrentUser)

  useEffect(() => {
    if (visible) {
      form.resetFields()
    }
  }, [visible, form])

  const handleSubmit = async (values: any) => {
    if (!currentUser?.id) {
      message.error(t('admin.cannotGetUserOrOrgInfo'))
      return
    }

    try {
      setLoading(true)

      const response = await OrganizationsService.Post.createOrganization([
        {
          companyName: values.companyName,
          companyId: values.companyId,
          billingAddress: values.billingAddress,
          billingAddress2: values.billingAddress2,
          billingCity: values.billingCity,
          billingState: values.billingState,
          billingZip: values.billingZip,
          duns: values.duns,
          naics: values.naics || '',
          phoneNumber: values.phoneNumber || '',
        },
      ])

      let newOrgId: string | undefined
      if (response && response.data) {
        if (Array.isArray(response.data) && response.data[0]?.guid) {
          newOrgId = response.data[0].guid
        } else if (response.data.guid) {
          newOrgId = response.data.guid
        }
      }

      if (!newOrgId) {
        message.error(t('admin.createCompanyFailed'))
        setLoading(false)
        return
      }

      await OrganizationsService.Post.addOrganizationUser(newOrgId, [
        currentUser.id,
      ])
      await OrganizationsService.Post.addOrganizationAdmin(
        newOrgId,
        currentUser.id,
      )

      message.success(t('admin.createCompanySuccess'))
      form.resetFields()
      onClose()

      if (onSuccess) {
        onSuccess()
      }
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || t('admin.createCompanyFailed'),
      )
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  return (
    <Modal
      title={t('admin.addNewCompany')}
      visible={visible}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={700}
      okText={t('admin.createCompany')}
      cancelText={t('admin.cancel')}
      loadingData={loading}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('admin.companyName')}
              name="companyName"
              rules={[
                { required: true, message: t('admin.pleaseEnterCompanyName') },
              ]}
            >
              <Input placeholder={t('admin.enterCompanyName')} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={t('admin.companyId')}
              name="companyId"
              rules={[
                { required: true, message: t('admin.pleaseEnterCompanyId') },
              ]}
            >
              <Input placeholder={t('admin.enterCompanyId')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item 
              label={t('admin.duns')} 
              name="duns"
              rules={[
                { required: true, message: t('admin.pleaseEnterDuns') },
              ]}
            >
              <Input placeholder={t('admin.enterDuns')} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label={t('admin.phone')} name="phoneNumber">
              <Input placeholder={t('admin.enterPhone')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label={t('admin.naics')} name="naics">
              <Input placeholder={t('admin.enterNaics')} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={t('admin.homeAddress')}
              name="billingAddress"
              rules={[{ required: true, message: t('admin.pleaseEnterAddress') }]}
            >
              <Input placeholder={t('admin.enterHomeAddress')} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label={t('admin.companyAddress')} name="billingAddress2">
          <Input placeholder={t('admin.enterCompanyAddress')} />
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label={t('admin.city')}
              name="billingCity"
              rules={[
                { required: true, message: t('admin.pleaseEnterCity') },
              ]}
            >
              <Input placeholder={t('admin.enterCity')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label={t('admin.state')} name="billingState">
              <Input placeholder={t('admin.enterState')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label={t('admin.zipCode')} name="billingZip">
              <Input placeholder={t('admin.enterZipCode')} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}